import { type ZodType } from 'zod'
import type { HttpMethod } from '@lib/constants/http-method'
import { parseApiErrorResponse, type ApiErrorResponse } from '@lib/dto/api-error.dto'
import { parseJsonText, type JsonObject, type JsonValue } from '@lib/helpers/json'
import { staffTokenStorage, tenantTokenStorage, type TokenStore } from './tokenStorage'
import logger, { toErrorMessage } from './logger'

/**
 * The fetch-backed HTTP client for the web app. Every response is validated
 * with a Zod schema at the network boundary — the client never launders a raw
 * `.json()` payload into a concrete type (`app/no-untyped-fetch-json`). The
 * schema is both the validator and the source of the returned type.
 *
 * Usage:
 *   const settings = await api.get('/dashboard/tenants/t1/settings', tenantSettingsDtoSchema) // TenantSettingsDto
 *   const maybe = await api.getNullable('/some/optional/resource', someDtoSchema) // SomeDto | null
 *
 * `get<T>` asserts the endpoint always returns a `T`. For endpoints that can
 * legitimately return no content (empty body / 204 / a bare `null`), use
 * `getNullable<T>`, which resolves an absent body to `null` and types the
 * result `T | null` (`app/prefer-get-nullable`).
 */

/** The base URL of the API. Overridable per environment via `VITE_API_URL`. */
export const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'

/**
 * Thrown for any non-2xx response (and for network failures, with `status: 0`).
 * Carries the HTTP status and the parsed error body so callers and React Query
 * error states can branch on `status` or surface `body.message`.
 */
export class ApiError extends Error {
  readonly status: number
  readonly body: ApiErrorResponse

  constructor(status: number, body: ApiErrorResponse) {
    const base = body.message ?? body.error ?? `Request failed with status ${status}`
    super(body.requestId ? `${base} (ref ${body.requestId})` : base)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

/** Type guard for {@link ApiError} that narrows without an `unknown` annotation. */
export function isApiError<E>(error: E): error is E & ApiError {
  return error instanceof ApiError
}

/**
 * Options accepted by every request method. The dashboard's own calls; the
 * published client's `RequestOptions` describes a caller's requests (query,
 * JSON, form) and deliberately doesn't share this shape.
 */
export interface RequestOptions {
  /** Extra headers merged over the defaults. */
  headers?: Record<string, string>
  /** AbortController signal for cancellation. */
  signal?: AbortSignal
}

function parseJson(text: string): JsonValue | null {
  return parseJsonText(text)
}

/** Extract a typed error body from a raw response text (never throws). */
function readErrorBody(text: string): ApiErrorResponse {
  if (text.trim() === '') return {}
  return parseApiErrorResponse(text) ?? { message: text }
}

/**
 * Perform the HTTP request, applying the base URL, auth header, and JSON body
 * encoding, and translating a failed response into a thrown {@link ApiError}.
 * Returns the raw response text so each method can parse it with its schema.
 */
async function send(
  tokens: TokenStore,
  method: HttpMethod,
  path: string,
  body: JsonObject | undefined,
  options: RequestOptions | undefined,
): Promise<string> {
  const headers: Record<string, string> = { ...options?.headers }
  if (body !== undefined && headers['Content-Type'] === undefined) {
    headers['Content-Type'] = 'application/json'
  }

  const token = tokens.get()
  if (token) headers['Authorization'] = `Bearer ${token}`

  let response: Response
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(options?.signal ? { signal: options.signal } : {}),
    })
  } catch (error) {
    // Rethrow caller-initiated cancellation so callers can detect it.
    if (error instanceof Error && error.name === 'AbortError') throw error
    const message = toErrorMessage(error)
    logger.error('Network request failed', { path, error: message })
    throw new ApiError(0, { message })
  }

  const text = await response.text()

  if (!response.ok) {
    // A 401 means the stored token is no longer valid — drop it so the app
    // falls back to the signed-out state on the next auth check.
    if (response.status === 401) tokens.clear()
    const errorBody = readErrorBody(text)
    logger.error('API request failed', {
      path,
      status: response.status,
      message: errorBody.message ?? errorBody.error ?? null,
      requestId: errorBody.requestId ?? null,
    })
    throw new ApiError(response.status, errorBody)
  }

  return text
}

/**
 * A client bound to one session's token store. `api` is the staff ops console
 * (and public signer routes, which ignore the header); `tenantApi` is the
 * tenant dashboard.
 */
function createApiClient(tokens: TokenStore) {
  return {
    /** GET a resource that is always present; validates the body with `schema`. */
    async get<T>(
      path: string,
      schema: ZodType<T>,
      options?: RequestOptions,
    ): Promise<T> {
      const text = await send(tokens, 'GET', path, undefined, options)
      return schema.parse(parseJson(text))
    },

    /**
     * GET a resource that may be absent. An empty body / 204 / bare `null`
     * resolves to `null`; otherwise the body is validated with `schema`.
     */
    async getNullable<T>(
      path: string,
      schema: ZodType<T>,
      options?: RequestOptions,
    ): Promise<T | null> {
      const text = await send(tokens, 'GET', path, undefined, options)
      const parsed = parseJson(text)
      if (parsed === null) return null
      return schema.parse(parsed)
    },

    /** POST a JSON body and validate the response with `schema`. */
    async post<T>(
      path: string,
      body: JsonObject,
      schema: ZodType<T>,
      options?: RequestOptions,
    ): Promise<T> {
      const text = await send(tokens, 'POST', path, body, options)
      return schema.parse(parseJson(text))
    },

    /** PUT a JSON body and validate the response with `schema`. */
    async put<T>(
      path: string,
      body: JsonObject,
      schema: ZodType<T>,
      options?: RequestOptions,
    ): Promise<T> {
      const text = await send(tokens, 'PUT', path, body, options)
      return schema.parse(parseJson(text))
    },

    /** PATCH a JSON body and validate the response with `schema`. */
    async patch<T>(
      path: string,
      body: JsonObject,
      schema: ZodType<T>,
      options?: RequestOptions,
    ): Promise<T> {
      const text = await send(tokens, 'PATCH', path, body, options)
      return schema.parse(parseJson(text))
    },

    /** DELETE a resource. Resolves once the server confirms; no body is returned. */
    async del(path: string, options?: RequestOptions): Promise<void> {
      await send(tokens, 'DELETE', path, undefined, options)
    },

    /** POST when the server returns 204 / an empty body. */
    async postEmpty(
      path: string,
      body: JsonObject = {},
      options?: RequestOptions,
    ): Promise<void> {
      await send(tokens, 'POST', path, body, options)
    },

    /** GET a binary body (PDFs). Does not parse JSON. */
    async getBlob(path: string, options?: RequestOptions): Promise<Blob> {
      const headers: Record<string, string> = { ...options?.headers }
      const token = tokens.get()
      if (token) headers['Authorization'] = `Bearer ${token}`

      let response: Response
      try {
        response = await fetch(`${API_BASE_URL}${path}`, {
          method: 'GET',
          headers,
          ...(options?.signal ? { signal: options.signal } : {}),
        })
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') throw error
        const message = toErrorMessage(error)
        logger.error('Network request failed', { path, error: message })
        throw new ApiError(0, { message })
      }

      if (!response.ok) {
        if (response.status === 401) tokens.clear()
        const text = await response.text()
        const errorBody = readErrorBody(text)
        logger.error('API request failed', {
          path,
          status: response.status,
          message: errorBody.message ?? errorBody.error ?? null,
        })
        throw new ApiError(response.status, errorBody)
      }

      return response.blob()
    },
  }
}

export type ApiClient = ReturnType<typeof createApiClient>

export const api = createApiClient(staffTokenStorage)

export const tenantApi = createApiClient(tenantTokenStorage)

/**
 * No session at all, for sign-in, password reset, and invitation endpoints. A
 * 401 there means a wrong password, not an expired session, so it must not
 * sign out whoever is already signed in.
 */
export const publicApi = createApiClient({
  get: () => null,
  set: () => undefined,
  clear: () => undefined,
})
